import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import ModalComponent from '../../../../components/ModalComponent';
import { Controller, useForm, useWatch } from 'react-hook-form';
import zod from 'zod';
import { Grid2 } from '@mui/material';
import CheckboxComponent from '../../../../components/CheckboxComponent';
import TelegramSelectComponent from '../../../../components/TelegramSelectComponent';
import ButtonComponent from '../../../../components/ButtonComponent';
import { zodResolver } from '@hookform/resolvers/zod';
import { ILprDeviceProps } from '../../../../services/Devices/LprService/types';
import { AppContext } from '../../../../context/AppContextProvider';
import WhatsAppSelectComponent from '../../../../components/WhatsAppSelectComponent';
import { IvsDeviceConfigEditionFormSchema } from '../../../../services/Devices/IvsService/schemas';
import { useUpdateConfigIvsDeviceAsync } from '../../../../services/Devices/IvsService/hooks/useMutation';
import MailSelectComponent from 'components/MailSelectComponent';
import { useSmtpIntegration } from '../../../../hooks/useSmtpIntegration';

interface IvsDeviceConfigModalProps {
  isOpen: boolean;
  selectedDevice?: any;
  setSelectedDevice?: Dispatch<SetStateAction<any>>;
  onClose?: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

type IvsDeviceConfigFormInput = zod.infer<
  typeof IvsDeviceConfigEditionFormSchema
>;

const IvsDeviceConfig = ({
  setIsOpen,
  setSelectedDevice,
  selectedDevice,
}: IvsDeviceConfigModalProps) => {
  const { control, handleSubmit, reset, setValue } = useForm<IvsDeviceConfigFormInput>({
    resolver: zodResolver(IvsDeviceConfigEditionFormSchema),
    defaultValues: {
      telegramIds: [],
      whatsAppNumbers: [],
      emailAddresses: [],
      isLpr: false,
      isSnapCam: false,
      snapTimer: null,
      isNotifyLogEvent: false,
      smtpIntegrationId: null,
    },
  });
  const { showSnackbar } = useContext(AppContext);
  const [selectedDeviceToConfig, setSelectedDeviceToConfig] =
    useState<ILprDeviceProps>();
  const { smtpValue, onSmtpChange } = useSmtpIntegration(control, setValue);

  const { mutate: mutateUpdateConfig } = useUpdateConfigIvsDeviceAsync();

  const handleClose = () => {
    setIsOpen(false);
    reset();
    if (setSelectedDevice) setSelectedDevice({});
  };

  const onSubmit = (data: IvsDeviceConfigFormInput) => {
    if (selectedDeviceToConfig) {
      mutateUpdateConfig(
        { data, id: selectedDeviceToConfig.id },
        {
          onSuccess: () => {
            showSnackbar('Configuração atualizada com sucesso.', 'success');
            if (setSelectedDevice) setSelectedDevice({});
            reset();
            setIsOpen(false);
          },
        },
      );
    }
  };

  useEffect(() => {
    if (selectedDevice) {
      reset({
        ...selectedDevice,
        smtpIntegrationId: selectedDevice?.smtpIntegrationId || null,
      });
      setSelectedDeviceToConfig(selectedDevice);
    } else {
      reset();
    }
  }, [selectedDevice, reset]);

  return (
    <Grid2 container columnSpacing={3}>
      <Controller
        render={({ field: { value, onChange } }) => (
          <WhatsAppSelectComponent
            gridSize={12}
            onChange={onChange}
            value={value}
          />
        )}
        control={control}
        name={'whatsAppNumbers'}
      />
      <Controller
        render={({ field: { value, onChange } }) => (
          <TelegramSelectComponent
            gridSize={12}
            onChange={onChange}
            value={value}
          />
        )}
        control={control}
        name={'telegramIds'}
      />
      <Controller
        render={({ field: { value, onChange } }) => (
          <MailSelectComponent
            onChange={onChange}
            value={value}
            smtpValue={smtpValue}
            onSmtpChange={onSmtpChange}
          />
        )}
        control={control}
        name={'emailAddresses'}
      />

      <Grid2 size={12}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="TimeLapse"
              onChange={onChange}
              value={value}
            />
          )}
          name={'isSnapCam'}
          control={control}
        />
      </Grid2>

      <Grid2 size={12}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="Leitura de placa"
              onChange={onChange}
              value={value}
            />
          )}
          name={'isLpr'}
          control={control}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="Eventos de Log"
              onChange={onChange}
              value={value}
            />
          )}
          name={'isNotifyLogEvent'}
          control={control}
        />
      </Grid2>
      <Grid2 size={12} sx={{ mt: 1 }}>
        <ButtonComponent fullWidth onClick={handleSubmit(onSubmit)}>
          Salvar
        </ButtonComponent>
      </Grid2>
    </Grid2>
  );
};

export default IvsDeviceConfig;
